import styled from "@emotion/styled";
import type { OptionDataValue } from "@stepfinance/react";
import { Breakpoints, ContextButton, red, StepCard, StepDropdown, StepInfoBox, StepPieChart } from "@stepfinance/react";
import { Col, Row } from "antd";
import SkeletonAvatar from "antd/lib/skeleton/Avatar";
import { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import shallow from "zustand/shallow";

import { ReactComponent as LockIcon } from "../../assets/lock.svg";
import type { PieDataItemWithKey } from "../../components/dashboard/PortfolioOverview/PieChartLegend";
import { LoadingErrorMessage } from "../../components/LoadingErrorMessage/LoadingErrorMessage";
import { LoadingTransactionsModal } from "../../components/LoadingTransactionsModal/LoadingTransactionsModal";
import { TextDescription, TextTitle } from "../../components/shared";
import { StepSkeleton } from "../../components/StepSkeleton/StepSkeleton";
import { TiersModal } from "../../components/TiersModal/TiersModal";
import { MonthCards } from "../../components/TransactionHistory/MonthCards";
import { UserTransactions } from "../../components/TransactionHistory/UserTransactions";
import { useLanguageContext } from "../../contexts/language/language";
import { useTransactionCacheStatus } from "../../hooks/queries/transactionHistory/useTransactionCacheStatus";
import { useTransactionMonthStats } from "../../hooks/queries/transactionHistory/useTransactionMonthStats";
import { useTransactionPlatformStats } from "../../hooks/queries/transactionHistory/useTransactionPlatformStats";
import { useTransactionTiers } from "../../hooks/queries/transactionHistory/useTransactionTiers";
import { useUserTier } from "../../hooks/queries/transactionHistory/useUserTier";
import { useTransactionHistoryStore } from "../../hooks/stores/transactionHistoryStore";
import { createRange, formatStringWithPlaceholders } from "../../utils/utils";

export type DateRange = [startDate: Date, endDate: Date];

export const CalendarHistoryView = () => {
  // const { trackPageView } = useMatomo();
  const cacheStatusQuery = useTransactionCacheStatus(true);
  const platformStatsQuery = useTransactionPlatformStats();
  const monthStatsQuery = useTransactionMonthStats();

  const {
    currentLanguageData: {
      pages: { transactionHistory },
    },
    languageSetting,
  } = useLanguageContext();

  const [selectedYear, yearsAvailable, setSelectedYear, setTiersModalOpen, setYearsAvailable] =
    useTransactionHistoryStore(
      (state) => [
        state.selectedYear,
        state.yearsAvailable,
        state.setSelectedYear,
        state.setTiersModalOpen,
        state.setYearsAvailable,
      ],
      shallow,
    );
  const { data: tiers } = useTransactionTiers(selectedYear);
  const { data: userTier, isLoading: isUserTierLoading } = useUserTier(selectedYear, tiers);

  const isFullTier = useMemo(() => {
    return Number(userTier?.totalTransactionsToUnlock) > 1_000_000;
  }, [userTier?.totalTransactionsToUnlock]);

  const totalTransactions = useMemo(() => {
    const monthStats = monthStatsQuery.data;
    if (monthStats) {
      return monthStats.reduce((accum, month) => {
        const { fee_payer_count } = month;
        return accum + fee_payer_count || 0;
      }, 0);
    }
    return 0;
  }, [monthStatsQuery]);

  const platformsList = useMemo(() => {
    if (platformStatsQuery.isSuccess && platformStatsQuery.data) {
      return platformStatsQuery.data.map((data) => data.group_name);
    }
    return [];
  }, [platformStatsQuery.data, platformStatsQuery.isSuccess]);

  const platformsBreakdown: PieDataItemWithKey[] = useMemo(() => {
    const platformStats = platformStatsQuery.data;
    if (!platformStats) {
      return [];
    }
    const result: PieDataItemWithKey[] = [];
    platformStats.forEach((platform) => {
      const { count, group_name } = platform;
      if (platformStats) {
        result.push({
          value: count,
          name: group_name,
          key: group_name,
          label: {
            show: false,
          },
        });
      }
    });

    return result;
  }, [platformStatsQuery]);

  const lockedTransactions = useMemo(() => {
    if (isFullTier) {
      return 0;
    } else if (userTier && totalTransactions > Number(userTier.totalTransactionsToUnlock)) {
      return totalTransactions - Number(userTier.totalTransactionsToUnlock);
    } else if (!userTier) {
      return totalTransactions;
    } else {
      return 0;
    }
  }, [isFullTier, totalTransactions, userTier]);

  const unlockedTransactions = useMemo(() => {
    if (isFullTier) {
      return "All transactions";
    } else if (userTier && Number(userTier.totalTransactionsToUnlock) <= 0) {
      return "--";
    } else if (userTier && totalTransactions > Number(userTier.totalTransactionsToUnlock)) {
      return totalTransactions - (totalTransactions - Number(userTier.totalTransactionsToUnlock));
    } else if (userTier && totalTransactions > 0) {
      return totalTransactions;
    } else {
      return 0;
    }
  }, [isFullTier, totalTransactions, userTier]);

  const hasLoadingErrors = platformStatsQuery.isError || monthStatsQuery.isError;
  const isLoading = platformStatsQuery.isLoading || monthStatsQuery.isLoading || isUserTierLoading;
  const isSuccess = platformStatsQuery.isSuccess && monthStatsQuery.isSuccess && !isUserTierLoading;

  const refetchUserInfo = () => {
    void platformStatsQuery.refetch();
    void cacheStatusQuery.refetch();
    void monthStatsQuery.refetch();
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear();

    if (cacheStatusQuery.data?.oldestCachedDateTime) {
      const oldestYear = new Date(cacheStatusQuery.data.oldestCachedDateTime).getFullYear();
      setYearsAvailable(createRange(currentYear, oldestYear));
    } else {
      setYearsAvailable([currentYear]);
    }
  }, [cacheStatusQuery.data?.oldestCachedDateTime, setYearsAvailable]);

  const formatTooltip = useCallback((value: OptionDataValue) => {
    return String(value);
  }, []);

  return (
    <CalendarHistoryContainer>
      <div className="year-container">
        <div className="free-transactions-container">
          {formatStringWithPlaceholders(
            transactionHistory.seeFreeTransactions,
            <strong>{transactionHistory.forFree}</strong>,
          )}{" "}
          {formatStringWithPlaceholders(
            transactionHistory.visitLatestPage,
            <Link to={"/" + languageSetting + "/history/latest"}>{transactionHistory.latestPage}</Link>,
          )}
        </div>
        <StepDropdown
          size="l"
          items={yearsAvailable.map((year) => ({ label: year.toString(), key: year.toString() }))}
          selectedItem={selectedYear.toString()}
          disabled={yearsAvailable.length === 0 || isLoading}
          onSelect={(info) => {
            setSelectedYear(Number(info.key));
          }}
        />
      </div>
      {hasLoadingErrors && (
        <div className="error-message-container">
          <LoadingErrorMessage onRetryClick={refetchUserInfo}>
            {transactionHistory.calendar.errorLoadingInformation}
          </LoadingErrorMessage>
        </div>
      )}
      {!hasLoadingErrors && isLoading && (
        <Row gutter={[15, 30]} justify="space-between" className="user-info-container">
          <Col xxl={8} xl={12} xs={24}>
            <UserInformationSkeleton />
          </Col>
          <Col xxl={8} xl={12} xs={24}>
            <UserInformationSkeleton />
          </Col>
          <Col xxl={8} xs={24}>
            <PlatformsBreakdownSkeleton />
          </Col>
        </Row>
      )}
      {!hasLoadingErrors && isSuccess && (
        <Row gutter={[15, 30]} justify="space-between" className="user-info-container">
          <Col xxl={platformsBreakdown.length <= 0 ? 12 : 8} xl={12} xs={24}>
            {userTier ? (
              <StepCard className="user-info">
                <StepCard.Header>
                  <div className="user-info-header">
                    <TextTitle>{userTier.name}</TextTitle>
                    <ContextButton onClick={() => setTiersModalOpen(true)}>
                      {transactionHistory.calendar.seeAvailableTiers}
                    </ContextButton>
                  </div>
                </StepCard.Header>
                <StepCard.Content>
                  <StepCard type="secondary">
                    <div className="info-boxes-container">
                      <StepInfoBox>
                        <StepInfoBox.Description>
                          <TextDescription>
                            {formatStringWithPlaceholders(
                              transactionHistory.calendar.transactionsMadeInYear,
                              selectedYear,
                            )}
                          </TextDescription>
                        </StepInfoBox.Description>
                        <StepInfoBox.Title>
                          <TextTitle number>{totalTransactions}</TextTitle>
                        </StepInfoBox.Title>
                      </StepInfoBox>
                      <StepInfoBox>
                        <StepInfoBox.Description>
                          <TextDescription>{transactionHistory.calendar.totalTransactions}</TextDescription>
                        </StepInfoBox.Description>
                        <StepInfoBox.Title>
                          <TextTitle number={!isFullTier}>
                            {isFullTier
                              ? transactionHistory.calendar.allTransactions
                              : userTier.totalTransactionsToUnlock}
                          </TextTitle>
                        </StepInfoBox.Title>
                      </StepInfoBox>
                    </div>
                  </StepCard>
                </StepCard.Content>
              </StepCard>
            ) : (
              <StepCard className="user-info">
                <StepCard.Header>
                  <TextTitle>
                    {formatStringWithPlaceholders(transactionHistory.calendar.noTier, selectedYear)}
                  </TextTitle>
                </StepCard.Header>
                <StepCard.Content>
                  <div className="buy-tier-content">
                    <div className="buy-tier-container">
                      <TextDescription>{transactionHistory.calendar.needToBuyTier}</TextDescription>
                      <TextDescription>{transactionHistory.calendar.tiersOnlyApply}</TextDescription>
                      <ContextButton onClick={() => setTiersModalOpen(true)}>
                        {transactionHistory.calendar.seeAvailableTiers}
                      </ContextButton>
                    </div>
                  </div>
                </StepCard.Content>
              </StepCard>
            )}
          </Col>
          <Col xxl={platformsBreakdown?.length <= 0 ? 12 : 8} xl={12} xs={24}>
            <StepCard className="user-info">
              <StepCard.Header>
                <div className="user-info-header">
                  <TextTitle size="s">{transactionHistory.calendar.transfersBreakdown}</TextTitle>
                </div>
              </StepCard.Header>
              <StepCard.Content>
                <StepCard type="secondary">
                  <div className="info-boxes-container">
                    <StepInfoBox>
                      <StepInfoBox.Description>
                        <TextDescription>
                          {formatStringWithPlaceholders(
                            transactionHistory.calendar.lockedTransactionsInYear,
                            selectedYear,
                          )}
                        </TextDescription>
                      </StepInfoBox.Description>
                      <StepInfoBox.Title>
                        <div className="locked-transactions-countdown-container">
                          <LockIcon className="locked-transactions-box-icon" />
                          <TextTitle className="locked-transactions-amount" number>
                            {lockedTransactions}
                          </TextTitle>
                          {userTier && totalTransactions > Number(userTier.totalTransactionsToUnlock) && !isFullTier && (
                            <div className="unlock-button-container">
                              <ContextButton onClick={() => setTiersModalOpen(true)}>
                                {transactionHistory.calendar.unlockButton}
                              </ContextButton>
                            </div>
                          )}
                        </div>
                      </StepInfoBox.Title>
                    </StepInfoBox>
                    <StepInfoBox>
                      <StepInfoBox.Description>
                        <TextDescription>
                          {formatStringWithPlaceholders(
                            transactionHistory.calendar.unlockedTransactionsInYear,
                            selectedYear,
                          )}
                        </TextDescription>
                      </StepInfoBox.Description>
                      <StepInfoBox.Title>
                        <div className="upgrade-container">
                          <TextTitle
                            number={!isFullTier}
                            disabled={
                              ((userTier && Number(userTier.totalTransactionsToUnlock) <= 0) ||
                                totalTransactions <= 0) &&
                              !isFullTier
                            }
                          >
                            {unlockedTransactions}
                          </TextTitle>
                        </div>
                      </StepInfoBox.Title>
                    </StepInfoBox>
                  </div>
                </StepCard>
              </StepCard.Content>
            </StepCard>
          </Col>
          {platformsBreakdown.length > 0 && (
            <Col xxl={8} xs={24}>
              <StepCard className="user-info">
                <StepCard.Header>
                  <div className="user-info-header">
                    <TextTitle size="s">{transactionHistory.calendar.totalTransactionsPerPlatforms}</TextTitle>
                  </div>
                </StepCard.Header>
                <StepCard.Content>
                  <StepCard className="pie-chart-card-container" type="secondary">
                    <div className="pie-card-container">
                      <StepPieChart
                        width={"100%"}
                        height={"100%"}
                        rendererOverride="svg"
                        data={platformsBreakdown}
                        tooltipValueFormatter={formatTooltip}
                        key={platformsBreakdown.length}
                        echartsOptions={{
                          legend: {
                            show: true,
                            right: "10%",
                            padding: platformsBreakdown.length > 7 ? [30, 0, 0, 0] : [15, 0, 15, 0],
                            pageTextStyle: { color: "#fff" },
                            type: "scroll",
                          },
                          series: {
                            radius: "80%",
                            center: ["30%", "50%"],
                          },
                        }}
                      />
                    </div>
                  </StepCard>
                </StepCard.Content>
              </StepCard>
            </Col>
          )}
        </Row>
      )}
      {!hasLoadingErrors && (
        <MonthCards
          isLoading={isLoading}
          isSuccess={isSuccess}
          monthStats={monthStatsQuery.data}
          userTierMaxTransactions={Number(userTier ? userTier.totalTransactionsToUnlock : 0)}
        />
      )}

      <UserTransactions
        showDownloadButton
        showDateFilter
        transactionsYear={selectedYear}
        hasUserATierForCurrentYear={!!userTier}
        totalTransactionsMade={totalTransactions}
        platforms={platformsList}
        isPageLoading={isLoading}
        lastTransactionUnlockedDate={userTier?.maxDateOfUnlock}
      />
      <LoadingTransactionsModal yearLoading={selectedYear} width={685} />
      <TiersModal />
    </CalendarHistoryContainer>
  );
};

const UserInformationSkeleton: React.FC = () => {
  return (
    <StepCard className="user-info">
      <StepCard.Header>
        <div className="user-info-header">
          <StepSkeleton width={115} />
        </div>
      </StepCard.Header>
      <StepCard.Content>
        <StepCard type="secondary">
          <div className="info-boxes-container">
            <StepInfoBox>
              <StepInfoBox.Description>
                <StepSkeleton size="s" width={225} />
              </StepInfoBox.Description>
              <StepInfoBox.Title>
                <StepSkeleton size="m" width={110} />
              </StepInfoBox.Title>
            </StepInfoBox>
            <StepInfoBox>
              <StepInfoBox.Description>
                <StepSkeleton size="s" width={225} />
              </StepInfoBox.Description>
              <StepInfoBox.Title>
                <StepSkeleton size="m" width={110} />
              </StepInfoBox.Title>
            </StepInfoBox>
          </div>
        </StepCard>
      </StepCard.Content>
    </StepCard>
  );
};

const PlatformsBreakdownSkeleton: React.FC = () => {
  return (
    <StepCard className="user-info">
      <StepCard.Header>
        <div className="user-info-header">
          <StepSkeleton width={115} />
        </div>
      </StepCard.Header>
      <StepCard.Content>
        <StepCard type="secondary">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <SkeletonAvatar size={190} />
          </div>
        </StepCard>
      </StepCard.Content>
    </StepCard>
  );
};

const CalendarHistoryContainer = styled.div`
  .year-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .free-transactions-container {
      @media (max-width: ${Breakpoints.S}px) {
        max-width: 330px;
      }
    }
  }
  .error-message-container {
    height: 200px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .user-info-container {
    margin-top: 20px;
    .user-info {
      height: 314px;
      .pie-chart-card-container {
        height: 100%;
        .pie-card-container {
          height: 100%;
          display: flex;
          justify-content: center;
        }
      }
      .buy-tier-content {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        .buy-tier-container {
          display: flex;
          gap: 20px;
          flex-direction: column;
          text-align: center;
          width: 225px;
        }
      }
    }
    .user-info-header {
      display: flex;
      justify-content: space-between;
      width: 100%;
      align-items: center;
      height: 36px;
    }
    .info-boxes-container {
      display: flex;
      flex-direction: column;
      gap: 10px;
      .upgrade-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .locked-transactions-countdown-container {
        display: flex;
        align-items: center;
        gap: 8px;
        .locked-transactions-box-icon {
          width: 20px;
          height: 20px;
          color: ${red};
        }
        .locked-transactions-amount {
          color: ${red};
        }
        .unlock-button-container {
          flex: 1;
          display: flex;
          justify-content: flex-end;
        }
      }
    }
  }
  .transactions-container {
    margin: 20px 0 32px 0;
    .title-container {
      margin-bottom: 30px;
    }
    .monthly-transactions-container {
      .monthly-transaction-box {
        padding: 10px;
        height: 120px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        .description-container {
          margin-top: 6px;
          .transactions-label {
            white-space: nowrap;
            display: flex;
            align-items: center;
          }
        }
        .see-more-button {
          margin-top: 10px;
        }
      }
    }
  }
`;
